import { API } from '../api.js';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import styles from './AdminPlansTab.module.css';
import ConfirmDialog from '../components/ConfirmDialog';
import useBackButton from '../hooks/useBackButton';

const EMPTY_PLAN = { name: '', name_th: '', price_monthly: 0, price_yearly: 0, description: '', description_th: '', features: [], features_th: [], max_courses: 3, max_communities: 1, max_storage_gb: 1, badge_color: '#6c63ff', is_active: true, sort_order: 0 };

export default function AdminPlansTab({ onToast }) {
  const { token } = useAuth();
  const { t, lang } = useLang();
  const [plans, setPlans]   = useState([]);
  const [modal, setModal]   = useState(false);
  const [editing, setEditing] = useState(null); // null = new
  const [form, setForm]     = useState(EMPTY_PLAN);
  const [saving, setSaving] = useState(false);
  const [subs, setSubs]     = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, planId: null });

  async function load() {
    const [pRes, sRes] = await Promise.all([
      fetch(`${API}/api/plans/all`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${API}/api/subscriptions`,  { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    if (pRes.ok) setPlans(await pRes.json());
    if (sRes.ok) setSubs(await sRes.json());
  }
  useEffect(() => { load(); }, []); // eslint-disable-line

  function openNew()    { setForm(EMPTY_PLAN); setEditing(null); setModal(true); }
  function openEdit(p)  {
    setForm({ ...p, features: p.features || [], features_th: p.features_th || [], is_active: !!p.is_active });
    setEditing(p.id); setModal(true);
  }
  function closeModal() { setModal(false); setEditing(null); }

  useBackButton(closeModal, modal);

  async function savePlan(e) {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      features:    typeof form.features === 'string'
        ? form.features.split('\n').map(s => s.trim()).filter(Boolean)
        : form.features,
      features_th: typeof form.features_th === 'string'
        ? form.features_th.split('\n').map(s => s.trim()).filter(Boolean)
        : form.features_th,
    };
    const url    = editing ? `${API}/api/plans/${editing}` : `${API}/api/plans`;
    const method = editing ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    if (res.ok) { await load(); closeModal(); onToast(t('common.success')); }
    else onToast((await res.json()).error || t('common.error'));
    setSaving(false);
  }

  function triggerDelete(id) {
    setConfirmDialog({ isOpen: true, planId: id });
  }

  async function confirmDelete() {
    const id = confirmDialog.planId;
    if (!id) return;
    const res = await fetch(`${API}/api/plans/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { await load(); onToast(t('common.success')); }
    else onToast((await res.json()).error || t('common.error'));
    setConfirmDialog({ isOpen: false, planId: null });
  }

  // Revenue stats
  const activeSubs = subs.filter(s => s.status === 'active');
  const mrr = plans.reduce((acc, p) => {
    const count = activeSubs.filter(s => s.plan_id === p.id).length;
    return acc + count * p.price_monthly;
  }, 0);

  return (
    <div className="flex-col">
      {/* Revenue Overview */}
      <div className={styles.statsGrid}>
        <div className={`${styles.card} ${styles.cardPurple}`}>
          <div className={styles.headerRow}>
            <span className={styles.label}>MRR</span>
            <div className={`${styles.iconWrapper} ${styles.purpleIcon}`}>💰</div>
          </div>
          <div className={styles.value}>฿{mrr.toLocaleString()}</div>
          <div className={styles.change}>Active: {plans.filter(p => p.is_active).length} plans</div>
        </div>

        <div className={`${styles.card} ${styles.cardGreen}`}>
          <div className={styles.headerRow}>
            <span className={styles.label}>Active Subscribers</span>
            <div className={`${styles.iconWrapper} ${styles.greenIcon}`}>👥</div>
          </div>
          <div className={styles.value}>{activeSubs.length}</div>
          <div className={styles.change}>Subscribed accounts</div>
        </div>

        <div className={`${styles.card} ${styles.cardOrange}`}>
          <div className={styles.headerRow}>
            <span className={styles.label}>Active Plans</span>
            <div className={`${styles.iconWrapper} ${styles.orangeIcon}`}>📋</div>
          </div>
          <div className={styles.value}>{plans.filter(p => p.is_active).length}</div>
          <div className={styles.change}>Published on site</div>
        </div>

        <div className={`${styles.card} ${styles.cardRed}`}>
          <div className={styles.headerRow}>
            <span className={styles.label}>ARR (Projected)</span>
            <div className={`${styles.iconWrapper} ${styles.redIcon}`}>📈</div>
          </div>
          <div className={styles.value}>฿{(mrr * 12).toLocaleString()}</div>
          <div className={styles.change}>Annualized revenue</div>
        </div>
      </div>

      {/* Plans table */}
      <div className={styles.tableCard}>
        <div className={styles.tableCardHeader}>
          <div className={styles.tableCardTitle}>📋 {t('adminPlans.title')}</div>
          <button className={styles.newPlanBtn} onClick={openNew}>+ {t('adminPlans.newPlan')}</button>
        </div>
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Plan</th><th>Monthly</th><th>Yearly</th>
                <th>{t('adminPlans.subscribers')}</th><th>Courses</th><th>Communities</th>
                <th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map(p => {
                const count = activeSubs.filter(s => s.plan_id === p.id).length;
                return (
                  <tr key={p.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: p.badge_color, flexShrink: 0 }} />
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--text)' }}>{lang === 'th' ? p.name_th : p.name}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: 2 }}>{p.description}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontWeight: 600, color: 'var(--accent3)' }}>฿{p.price_monthly.toLocaleString()}</td>
                    <td style={{ color: 'var(--text2)' }}>฿{p.price_yearly.toLocaleString()}</td>
                    <td><span className={styles.countBadge}>{count}</span></td>
                    <td>{p.max_courses === -1 ? '∞' : p.max_courses}</td>
                    <td>{p.max_communities === -1 ? '∞' : p.max_communities}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${p.is_active ? styles.activeBadge : styles.inactiveBadge}`}>
                        <span className={styles.statusDot} />
                        {p.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className={styles.editBtn} onClick={() => openEdit(p)}>✏️ {t('common.edit')}</button>
                        <button className={styles.deleteBtn} onClick={() => triggerDelete(p.id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <div className={styles.modalOverlay} onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className={styles.modal} style={{ width: 'min(580px, 100%)' }}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>{editing ? t('adminPlans.editPlan') : t('adminPlans.newPlan')}</div>
              <button className={styles.modalClose} onClick={closeModal}>×</button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={savePlan}>
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">{t('adminPlans.planName')}</label>
                    <input className="form-input" required value={form.name} onChange={e => setForm(f=>({...f, name: e.target.value}))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('adminPlans.planNameTh')}</label>
                    <input className="form-input" required value={form.name_th} onChange={e => setForm(f=>({...f, name_th: e.target.value}))} />
                  </div>
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">{t('adminPlans.priceMonthly')}</label>
                    <input type="number" className="form-input" min="0" value={form.price_monthly} onChange={e => setForm(f=>({...f, price_monthly: +e.target.value}))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('adminPlans.priceYearly')}</label>
                    <input type="number" className="form-input" min="0" value={form.price_yearly} onChange={e => setForm(f=>({...f, price_yearly: +e.target.value}))} />
                  </div>
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">{t('adminPlans.description')}</label>
                    <input className="form-input" value={form.description} onChange={e => setForm(f=>({...f, description: e.target.value}))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('adminPlans.descriptionTh')}</label>
                    <input className="form-input" value={form.description_th} onChange={e => setForm(f=>({...f, description_th: e.target.value}))} />
                  </div>
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">{t('adminPlans.features')}</label>
                    <textarea className="form-input" style={{ minHeight: 60 }}
                      value={Array.isArray(form.features) ? form.features.join('\n') : form.features}
                      onChange={e => setForm(f=>({...f, features: e.target.value}))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('adminPlans.featuresTh')}</label>
                    <textarea className="form-input" style={{ minHeight: 60 }}
                      value={Array.isArray(form.features_th) ? form.features_th.join('\n') : form.features_th}
                      onChange={e => setForm(f=>({...f, features_th: e.target.value}))} />
                  </div>
                </div>
                <div className="form-row-3">
                  <div className="form-group">
                    <label className="form-label">{t('adminPlans.maxCourses')}</label>
                    <input type="number" className="form-input" value={form.max_courses} onChange={e => setForm(f=>({...f, max_courses: +e.target.value}))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Max Communities</label>
                    <input type="number" className="form-input" value={form.max_communities} onChange={e => setForm(f=>({...f, max_communities: +e.target.value}))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('adminPlans.maxStorage')}</label>
                    <input type="number" className="form-input" min="1" value={form.max_storage_gb} onChange={e => setForm(f=>({...f, max_storage_gb: +e.target.value}))} />
                  </div>
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">{t('adminPlans.badgeColor')}</label>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="color" value={form.badge_color} onChange={e => setForm(f=>({...f, badge_color: e.target.value}))} style={{ width: 36, height: 30, borderRadius: 4, border: '1px solid var(--border)', cursor: 'pointer', background: 'none', padding: 2 }} />
                      <input className="form-input" style={{ padding: '4px 8px', fontSize: 12, height: 30 }} value={form.badge_color} onChange={e => setForm(f=>({...f, badge_color: e.target.value}))} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('adminPlans.isActive')}</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!form.is_active} onChange={e => setForm(f=>({...f, is_active: e.target.checked}))} style={{ width: 16, height: 16 }} />
                      <span style={{ fontSize: 12, color: 'var(--text2)' }}>{t('adminPlans.isActive')}</span>
                    </label>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 12 }}>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={closeModal}>{t('common.cancel')}</button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? t('common.loading') : t('adminPlans.save')}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {confirmDialog.isOpen && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={t('adminPlans.deleteTitle', 'Delete Pricing Plan')}
          message={t('adminPlans.confirmDelete', 'Are you sure you want to delete this pricing plan?')}
          confirmText={t('common.delete', 'Delete')}
          cancelText={t('common.cancel', 'Cancel')}
          onConfirm={confirmDelete}
          onCancel={() => setConfirmDialog({ isOpen: false, planId: null })}
          type="danger"
        />
      )}
    </div>
  );
}
